import {useFSMStore} from 'stores/useFSMStore';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useCameraStore} from 'stores/useCameraStore';
import {useFrame} from '@react-three/fiber';
import {easeOutCubic} from 'utils/Easings';
import {useControlsStore} from 'stores/useControlsStore';
import {MathUtils, PerspectiveCamera, Vector3} from 'three';
import {useSizeClassStore} from 'stores/useSizeClassStore';

export const useCameraController = (): void => {
  const fovMultiplier = useSizeClassStore(state => state.fovMultiplier);
  const currentFSMState = useFSMStore(state => state.currentFSMState);
  const {cameraBrain, activeVirtualCamera, setActiveVirtualCamera, rawFov, setRawFov} = useCameraStore(state => ({
    cameraBrain: state.cameraBrain,
    rawFov: state.rawFov,
    setRawFov: state.setRawFov,
    activeVirtualCamera: state.activeVirtualCamera,
    setActiveVirtualCamera: state.setActiveVirtualCamera
  }));
  const {setControlsStoreConfig, setControlsTarget} = useControlsStore(state => ({
    setControlsStoreConfig: state.setConfig,
    setControlsTarget: state.setTarget
  }));

  //#region Lerping between Cameras

  const [isLerping, setLerping] = useState<boolean>(false);
  const lerpDuration = useRef<number>(-1);
  const lerpTime = useRef<number>(-1);
  const orbitControlsEnabled = useRef<boolean>(false);
  const previousCamera = useRef<PerspectiveCamera>(new PerspectiveCamera());
  const nextTargetPosition = useRef<Vector3>(new Vector3());

  const prepareForLerping =
    useCallback(() => {
      if (!cameraBrain || !activeVirtualCamera) return;

      lerpDuration.current = activeVirtualCamera.userData.tags?.lerpDuration || 0;
      orbitControlsEnabled.current = activeVirtualCamera.userData.tags?.orbitControls || false;
      nextTargetPosition.current = activeVirtualCamera.userData.tags?.cameraTarget?.position || null; // cameraTarget is Object3D

      previousCamera.current = cameraBrain.clone(); // keep track of previous cam for lerping
      setControlsStoreConfig({enabled: false, enableDamping: false});
      setLerping(true);
      lerpTime.current = 0;

    }, [activeVirtualCamera, cameraBrain, setControlsStoreConfig]);

  const lerpCameraTransform =
    useCallback((target: PerspectiveCamera, from: PerspectiveCamera, to: PerspectiveCamera, t: number) => {
      target.position.lerpVectors(from.position, to.position, t);
      target.quaternion.slerpQuaternions(from.quaternion, to.quaternion, t); // overriden by target
      target.scale.lerpVectors(from.scale, to.scale, t);
      setRawFov(to.fov);
      target.fov = MathUtils.lerp(from.fov, to.fov * fovMultiplier, t);

      setControlsTarget(calculateTarget(target)); // target 0.01 m in front of the camera
    }, [fovMultiplier, setControlsTarget, setRawFov]);

  const copyCameraTransform =
    useCallback((target: PerspectiveCamera, source: PerspectiveCamera) => {
      // position
      target.position.copy(source.position);

      // rotation
      const cameraTarget = source.userData.tags?.cameraTarget;
      if (cameraTarget) target.lookAt(cameraTarget.position);
      else target.quaternion.copy(source.quaternion.clone());

      // scale
      target.scale.copy(source.scale);
      setRawFov(source.fov);
      target.fov = source.fov * fovMultiplier;
    }, [fovMultiplier, setRawFov]);

  // FOV
  useEffect(() => {
    if (cameraBrain) cameraBrain.fov = rawFov * fovMultiplier;
  }, [cameraBrain, fovMultiplier, rawFov]);

  useFrame((state, delta) => {
    if (cameraBrain && activeVirtualCamera) {
      if (isLerping) {
        let finishedLerping = !isLerping;

        lerpTime.current += delta; //increment timer once per frame

        // lerping finished
        if (lerpTime.current >= lerpDuration.current) {
          lerpTime.current = lerpDuration.current;
          finishedLerping = true;
        }

        // move camera
        const t = lerpDuration.current === 0 ? 1 : easeOutCubic(lerpTime.current / lerpDuration.current);
        lerpCameraTransform(cameraBrain, previousCamera.current, activeVirtualCamera, t);

        // set target and re-enable controls when finished
        if (finishedLerping) {
          setLerping(false);

          setControlsTarget(nextTargetPosition.current ? nextTargetPosition.current : calculateTarget(activeVirtualCamera));

          setControlsStoreConfig({
            enabled: orbitControlsEnabled.current || false,
            enableDamping: orbitControlsEnabled.current || false
          });
        }
      } else {
        if (!orbitControlsEnabled.current)
          copyCameraTransform(cameraBrain, activeVirtualCamera);
      }

      cameraBrain.updateProjectionMatrix();
    }
  });

  //#endregion

  // Set isLerping to true to
  useEffect(() => {
    prepareForLerping();
  }, [activeVirtualCamera, prepareForLerping]);

  // Update activeVirtualCamera when FSMState changes
  useEffect(() => {
    setActiveVirtualCamera(currentFSMState);
  }, [currentFSMState, setActiveVirtualCamera]);

};

/*** @returns {Vector3} */
const calculateTarget = (camera: PerspectiveCamera, distance = 0.01): Vector3 =>
  (new Vector3()).addVectors(
    camera.position,
    camera.getWorldDirection(new Vector3()).multiplyScalar(distance)
  );
